<script setup lang="ts">
import { computed } from 'vue'
import type { Clue } from '@/types'
import { useCluesStore } from '@/stores/clues'
import FosSelect from '@/components/ui/FosSelect.vue'
import type { Timestamp } from 'firebase/firestore'

const props = defineProps<{
  clue: Clue
  index: number
  incidentId: string
}>()

const cluesStore = useCluesStore()

const statusOptions = [
  { label: 'Unconfirmed', value: 'unconfirmed' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Resolved', value: 'resolved' },
]

const statusBadgeClass = computed(() => {
  switch (props.clue.status) {
    case 'confirmed': return 'bg-blue-950 text-blue-300'
    case 'resolved': return 'bg-green-950 text-green-300'
    default: return 'bg-amber-950 text-amber-300'
  }
})

const statusLabel = computed(() => {
  return statusOptions.find((o) => o.value === props.clue.status)?.label ?? props.clue.status
})

const formattedTime = computed(() => {
  const ts = props.clue.found_at as Timestamp | null
  if (!ts) return '—'
  const d = ts.toDate ? ts.toDate() : new Date(ts as unknown as number)
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
})

const coords = computed(() => {
  if (props.clue.lat != null && props.clue.lng != null) {
    return `${props.clue.lat.toFixed(4)}, ${props.clue.lng.toFixed(4)}`
  }
  return '—'
})

async function onStatusChange(newStatus: string) {
  await cluesStore.updateClueStatus(props.incidentId, props.clue.clue_id, newStatus as Clue['status'])
}
</script>

<template>
  <tr class="border-b border-outline-variant/15 hover:bg-surface-container-low transition-colors">
    <td class="px-4 py-3 text-sm text-outline font-mono">{{ index + 1 }}</td>
    <td class="px-4 py-3 text-sm text-on-surface max-w-xs">
      <p class="line-clamp-2">{{ clue.description }}</p>
    </td>
    <td class="px-4 py-3 text-sm">
      <div class="flex flex-col gap-1">
        <span v-if="clue.lat != null && clue.lng != null" class="text-on-surface-variant font-mono text-xs">{{ coords }}</span>
        <span v-else class="text-outline">—</span>
      </div>
    </td>
    <td class="px-4 py-3 text-sm text-on-surface-variant">{{ clue.found_by || '—' }}</td>
    <td class="px-4 py-3 text-sm text-on-surface-variant whitespace-nowrap font-mono">{{ formattedTime }}</td>
    <td class="px-4 py-3">
      <span :class="['inline-flex items-center px-2 py-0.5 text-xs font-label uppercase tracking-widest', statusBadgeClass]">
        {{ statusLabel }}
      </span>
    </td>
    <td class="px-4 py-3">
      <FosSelect
        :model-value="clue.status"
        :options="statusOptions"
        placeholder="Change status"
        class="w-36"
        @update:model-value="onStatusChange"
      />
    </td>
  </tr>
</template>
